import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

interface Customer {
  id: number;
  company_name: string;
  contact_name: string;
  email: string;
}

interface Invoice {
  id: number;
  invoice_number: string;
  total_amount: number;
  balance_due: number;
  total_refunds?: number;
  amount_paid?: number;
  status: string;
}

interface PaymentFormData {
  customer_id: number | null;
  invoice_id: number | null;
  amount: string;
  payment_method: string;
  payment_type: string; // 'full', 'partial', 'advance', 'batch'
  payment_date: string;
  reference_number: string;
  notes: string;
  status: string;
}

interface InvoiceAllocation {
  invoice_id: number;
  invoice_number: string;
  total_amount: number;
  balance_due: number;
  total_refunds?: number;
  allocated_amount: number;
}

const PaymentForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useSettings();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoiceAllocations, setInvoiceAllocations] = useState<InvoiceAllocation[]>([]);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const [formData, setFormData] = useState<PaymentFormData>({
    customer_id: null,
    invoice_id: null,
    amount: '',
    payment_method: '',
    payment_type: 'full',
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: '',
    status: 'completed',
  });

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchCustomers();
      if (isEdit) {
        fetchPayment();
      } else {
        // Check for customerId in URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const customerIdFromUrl = urlParams.get('customerId');
        if (customerIdFromUrl) {
          const customerId = parseInt(customerIdFromUrl);
          handleInputChange('customer_id', customerId);
        }
      }
    }
  }, [isAuthenticated, authLoading, id]);

  useEffect(() => {
    if (formData.customer_id) {
      // In edit mode, invoices are fetched inside fetchPayment
      if (!isEdit) {
        fetchCustomerInvoices(formData.customer_id);
      }

      // Set selected customer if not already set
      if (!selectedCustomer && customers.length > 0) {
        const customer = customers.find(c => c.id === formData.customer_id);
        if (customer) {
          setSelectedCustomer(customer);
        }
      }
    }
  }, [formData.customer_id, customers, selectedCustomer]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      if (response.data.success) {
        setCustomers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchCustomerInvoices = async (customerId: number) => {
    try {
      const response = await api.get(`/payments/customer/${customerId}/unpaid-invoices`);
      if (response.data.success) {
        setInvoices(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching customer invoices:', error);
    }
  };

  const fetchPayment = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/payments/${id}`);
      if (response.data.success) {
        const payment = response.data.data;
        setFormData({
          customer_id: payment.customer_id,
          invoice_id: payment.invoice_id,
          amount: payment.amount.toString(),
          payment_method: payment.payment_method,
          payment_type: payment.payment_type || (payment.invoice_id ? 'full' : 'advance'),
          payment_date: payment.payment_date.split('T')[0],
          reference_number: payment.reference_number || '',
          notes: payment.notes || '',
          status: payment.status,
        });

        // Set selected customer
        const customer = customers.find(c => c.id === payment.customer_id);
        if (customer) {
          setSelectedCustomer(customer);
        }

        // Fetch invoices and set selected invoice
        if (payment.customer_id) {
          try {
            const invoicesRes = await api.get(`/payments/customer/${payment.customer_id}/unpaid-invoices`);
            if (invoicesRes.data.success) {
              let invoiceList: Invoice[] = invoicesRes.data.data;

              if (payment.invoice_id) {
                let matchedInvoice = invoiceList.find((i: Invoice) => i.id === payment.invoice_id);

                if (!matchedInvoice) {
                  // Invoice is fully paid so not in unpaid list — construct from payment data
                  const allocation = payment.allocations?.find((a: any) => a.invoice_id === payment.invoice_id);
                  matchedInvoice = {
                    id: payment.invoice_id,
                    invoice_number: payment.invoice_number,
                    total_amount: allocation ? parseFloat(allocation.invoice_total) : parseFloat(payment.amount),
                    balance_due: 0,
                    status: 'paid',
                  };
                  invoiceList = [...invoiceList, matchedInvoice];
                }

                setInvoices(invoiceList);
                setSelectedInvoice(matchedInvoice);
              } else {
                setInvoices(invoiceList);
              }
            }
          } catch (e) {
            console.error('Error fetching invoices for payment:', e);
          }
        }
      }
    } catch (error: any) {
      console.error('Error fetching payment:', error);
      setError(error.response?.data?.message || 'Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PaymentFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleCustomerChange = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    setSelectedInvoice(null);
    handleInputChange('customer_id', customer?.id || null);
    handleInputChange('invoice_id', null);
    handleInputChange('amount', '');
    setInvoices([]);
  };

  const handleInvoiceChange = (invoice: Invoice | null) => {
    setSelectedInvoice(invoice);
    handleInputChange('invoice_id', invoice?.id || null);
    if (invoice && formData.payment_type === 'full') {
      handleInputChange('amount', invoice.balance_due.toString());
    }
  };

  const handlePaymentTypeChange = (paymentType: string) => {
    handleInputChange('payment_type', paymentType);
    
    // Reset invoice selection and amount based on payment type
    if (paymentType === 'advance') {
      setSelectedInvoice(null);
      handleInputChange('invoice_id', null);
      handleInputChange('amount', '');
    } else if (paymentType === 'batch') {
      setSelectedInvoice(null);
      handleInputChange('invoice_id', null);
      initializeBatchAllocations();
    } else if (paymentType === 'full' && selectedInvoice) {
      handleInputChange('amount', selectedInvoice.balance_due.toString());
    }
  };

  const initializeBatchAllocations = () => {
    // Initialize batch allocations with all unpaid invoices
    const allocations: InvoiceAllocation[] = invoices.map(invoice => ({
      invoice_id: invoice.id,
      invoice_number: invoice.invoice_number,
      total_amount: invoice.total_amount,
      balance_due: invoice.balance_due,
      total_refunds: invoice.total_refunds,
      allocated_amount: 0
    }));
    setInvoiceAllocations(allocations);
  };

  const handleAllocationChange = (invoiceId: number, amount: number) => {
    setInvoiceAllocations(prev => 
      prev.map(allocation => 
        allocation.invoice_id === invoiceId 
          ? { ...allocation, allocated_amount: amount }
          : allocation
      )
    );
    
    // Update total payment amount
    const totalAllocated = invoiceAllocations.reduce((sum, allocation) => 
      allocation.invoice_id === invoiceId ? sum + amount : sum + allocation.allocated_amount, 0
    );
    handleInputChange('amount', totalAllocated.toString());
  };

  const validateForm = (): boolean => {
    console.log('Validating form data:', formData);
    
    if (!formData.customer_id) {
      setError('Please select a customer');
      return false;
    }
    
    if (!formData.payment_type) {
      setError('Please select a payment type');
      return false;
    }
    
    if (formData.payment_type !== 'advance' && !formData.invoice_id && formData.payment_type !== 'batch') {
      setError('Please select an invoice');
      return false;
    }
    
    if (formData.payment_type === 'batch') {
      const totalAllocated = invoiceAllocations.reduce((sum, allocation) => sum + allocation.allocated_amount, 0);
      if (totalAllocated <= 0) {
        setError('Please allocate payment amounts to invoices');
        return false;
      }
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid payment amount');
      return false;
    }
    
    if (!formData.payment_method) {
      setError('Please select a payment method');
      return false;
    }
    
    if (!formData.payment_date) {
      setError('Please select a payment date');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const submitData: any = {
        ...formData,
        amount: parseFloat(formData.amount),
      };

      // Add allocations for batch payments
      if (formData.payment_type === 'batch') {
        submitData.allocations = invoiceAllocations
          .filter(allocation => allocation.allocated_amount > 0)
          .map(allocation => ({
            invoice_id: allocation.invoice_id,
            allocated_amount: allocation.allocated_amount
          }));
      }

      console.log('Submitting payment data:', submitData);

      if (isEdit) {
        await api.put(`/payments/${id}`, submitData);
        setSuccess('Payment updated successfully');
      } else {
        await api.post('/payments', submitData);
        setSuccess('Payment recorded successfully');
      }

      // Redirect after short delay to show success message
      setTimeout(() => {
        navigate('/payments');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving payment:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Failed to save payment');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Please log in to record payments.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/payments')}
          variant="outlined"
        >
          Back to Payments
        </Button>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {isEdit ? 'Edit Payment' : 'Record New Payment'}
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Main Form */}
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Customer Selection */}
                <Box>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => `${option.company_name} (${option.contact_name})`}
                    value={selectedCustomer}
                    onChange={(_, value) => handleCustomerChange(value)}
                    disabled={isEdit}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />,
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <li key={key} {...otherProps}>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {option.company_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.contact_name} • {option.email}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                  />
                </Box>

                {/* Payment Type Selection */}
                <Box>
                  <FormControl fullWidth required>
                    <InputLabel>Payment Type</InputLabel>
                    <Select
                      value={formData.payment_type}
                      label="Payment Type"
                      onChange={(e) => handlePaymentTypeChange(e.target.value)}
                      disabled={!formData.customer_id}
                    >
                      <MenuItem value="full">Full Payment</MenuItem>
                      <MenuItem value="partial">Partial Payment</MenuItem>
                      <MenuItem value="advance">Advance Payment</MenuItem>
                      <MenuItem value="batch">Batch Payment</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Invoice Selection - Show only for Full/Partial payments */}
                {formData.payment_type !== 'advance' && formData.payment_type !== 'batch' && (
                  <Box>
                    <Autocomplete
                      options={invoices}
                      getOptionLabel={(option) => `${option.invoice_number} - ${formatCurrency(option.balance_due)} due`}
                      value={selectedInvoice}
                      onChange={(_, value) => handleInvoiceChange(value)}
                      disabled={!formData.customer_id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Invoice"
                          required
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: <ReceiptIcon sx={{ mr: 1, color: 'action.active' }} />,
                          }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <li key={key} {...otherProps}>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {option.invoice_number}
                              </Typography>
                              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                <Typography variant="caption" color="text.secondary">
                                  Total: {formatCurrency(option.total_amount)}
                                </Typography>
                                {option.total_refunds && option.total_refunds > 0 && (
                                  <Chip
                                    label={`${formatCurrency(option.total_refunds)} refunded`}
                                    size="small"
                                    color="info"
                                  />
                                )}
                                <Chip
                                  label={`${formatCurrency(option.balance_due)} due`}
                                  size="small"
                                  color="warning"
                                />
                              </Box>
                            </Box>
                          </li>
                        );
                      }}
                    />
                  </Box>
                )}

                {/* Batch Payment Invoice Allocation */}
                {formData.payment_type === 'batch' && invoiceAllocations.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Invoice Allocation
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                      {invoiceAllocations.map((allocation) => (
                        <Box key={allocation.invoice_id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="body2" fontWeight="medium">
                              {allocation.invoice_number}
                            </Typography>
                            <Box display="flex" gap={1} alignItems="center">
                              {allocation.total_refunds && allocation.total_refunds > 0 && (
                                <Typography variant="caption" color="info.main">
                                  -{formatCurrency(allocation.total_refunds)} refunded
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                Balance: {formatCurrency(allocation.balance_due)}
                              </Typography>
                            </Box>
                          </Box>
                          <TextField
                            fullWidth
                            label="Payment Amount"
                            type="number"
                            value={allocation.allocated_amount}
                            onChange={(e) => handleAllocationChange(allocation.invoice_id, parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, max: allocation.balance_due, step: 0.01 }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1 }}>{getCurrencySymbol()}</Typography>,
                            }}
                            size="small"
                          />
                        </Box>
                      ))}
                    </Paper>
                  </Box>
                )}

                {/* Amount and Payment Method Row */}
                <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label="Payment Amount"
                      type="number"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      required
                      inputProps={{ min: 0.01, step: 0.01 }}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>{getCurrencySymbol()}</Typography>,
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={formData.payment_method}
                      label="Payment Method"
                      onChange={(e) => handleInputChange('payment_method', e.target.value)}
                    >
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                      <MenuItem value="credit_card">Credit Card</MenuItem>
                      <MenuItem value="debit_card">Debit Card</MenuItem>
                      <MenuItem value="check">Check</MenuItem>
                      <MenuItem value="online">Online Payment</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  </Box>
                </Box>

                {/* Payment Date and Status Row */}
                <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label="Payment Date"
                      type="date"
                      value={formData.payment_date}
                      onChange={(e) => handleInputChange('payment_date', e.target.value)}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                  </Box>
                </Box>

                {/* Reference Number */}
                <Box>
                  <TextField
                    fullWidth
                    label="Reference Number"
                    value={formData.reference_number}
                    onChange={(e) => handleInputChange('reference_number', e.target.value)}
                    placeholder="Bank reference, check number, transaction ID, etc."
                  />
                </Box>

                {/* Notes */}
                <Box>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Additional notes about this payment..."
                  />
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Summary Card */}
          <Box sx={{ flex: '0 0 350px', minWidth: 0 }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Summary
                </Typography>
                
                {selectedCustomer && (
                  <>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Customer
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedCustomer.company_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedCustomer.contact_name}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                  </>
                )}

                {selectedInvoice && (
                  <>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Invoice
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedInvoice.invoice_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Original Total: {formatCurrency(selectedInvoice.total_amount + (selectedInvoice.total_refunds || 0))}
                      </Typography>
                      {selectedInvoice.total_refunds && selectedInvoice.total_refunds > 0 && (
                        <Typography variant="caption" color="info.main" display="block">
                          Refunds: -{formatCurrency(selectedInvoice.total_refunds)}
                        </Typography>
                      )}
                      <Typography variant="caption" color="warning.main" fontWeight="medium" display="block">
                        Balance Due: {formatCurrency(selectedInvoice.balance_due)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                  </>
                )}

                {formData.amount && (
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Amount
                    </Typography>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      {formatCurrency(parseFloat(formData.amount) || 0)}
                    </Typography>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box display="flex" flexDirection="column" gap={2} mt={3}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    fullWidth
                  >
                    {saving ? 'Saving...' : isEdit ? 'Update Payment' : 'Record Payment'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/payments')}
                    startIcon={<CancelIcon />}
                    fullWidth
                  >
                    Cancel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default PaymentForm;